import { format } from "date-fns";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { db } from "../firebase";

import SelectMenu from "./crm/SelectMenu";
const reminderOptions = [
  { label: "5 minutes before", value: 5 },
  { label: "10 minutes before", value: 10 },
  { label: "30 minutes before", value: 30 },
  { label: "1 hour before", value: 60 },
  { label: "1 day before", value: 1440 },
];

function ReminderModal({
  showModal,
  setShowModal,
  event,
  onSendReminders,
  currentUserId,
  isSending,
}) {
  const [selectedOption, setSelectedOption] = useState({
    type: "search",
    value: "name",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState(5);
  const searchTimeoutRef = useRef(null);
  const [customOffset, setCustomOffset] = useState("");
  const [customUnit, setCustomUnit] = useState("minutes");
  const [useCustomOffset, setUseCustomOffset] = useState(false);
  const [useSpecificTime, setUseSpecificTime] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [validExternalNumbers, setValidExternalNumbers] = useState([]);
  const [externalNumbers, setExternalNumbers] = useState("");
  const [externalInput, setExternalInput] = useState("");
  const [sendToSelfOnly, setSendToSelfOnly] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);

  const isValidPhoneNumber = (number) => /^\+\d+$/.test(number);

  const searchOptions = [
    { label: "Search by Name", value: "search-name" },
    { label: "Search by Email", value: "search-email" },
    { label: "Search by Mobile", value: "search-mobile" },
    { label: "External Numbers", value: "external" },
  ];

  const parseAndValidateNumbers = (input) => {
    const numbers = input.split(",").map((n) => n.trim());
    return numbers.filter((number) => /^\+\d+$/.test(number));
  };

  // Commit whatever is currently typed in the external-number input into chips.
  const commitExternalInput = () => {
    const trimmed = externalInput.trim();
    if (!trimmed) return;
    const committed = externalNumbers
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
    const incoming = parseAndValidateNumbers(trimmed);
    // Keep only new, valid, non-duplicate numbers.
    const merged = [...committed];
    incoming.forEach((num) => {
      if (!merged.includes(num)) merged.push(num);
    });
    setExternalNumbers(merged.join(","));
    setExternalInput("");
  };

  const removeExternalNumber = (numberToRemove) => {
    const remaining = externalNumbers
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n && n !== numberToRemove);
    setExternalNumbers(remaining.join(","));
  };

  useEffect(() => {
    if (selectedOption.type === "external") {
      const validNumbers = parseAndValidateNumbers(externalNumbers);
      setValidExternalNumbers(validNumbers);
    } else {
      setValidExternalNumbers([]);
    }
  }, [externalNumbers, selectedOption]);

  const calculateOffsetMinutes = () => {
    if (useCustomOffset) {
      const offset = parseInt(customOffset, 10);
      if (isNaN(offset) || offset <= 0) return 0;
      switch (customUnit) {
        case "hours":
          return offset * 60;
        case "days":
          return offset * 60 * 24;
        default:
          return offset;
      }
    }
    return reminderOffsetMinutes;
  };

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (showModal && currentUserId) {
      const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
          const [lawyersSnapshot, clientsSnapshot] = await Promise.all([
            getDocs(collection(db, "lawyers")),
            getDocs(collection(db, "client")),
          ]);
          const lawyersData = lawyersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), userType: "lawyer" }));
          const clientsData = clientsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), userType: "client" }));
          const usersData = [...lawyersData, ...clientsData].filter((user) => user.id !== currentUserId);

          let currentUserDoc = await getDoc(
            doc(db, "lawyers", currentUserId)
          );
          if (!currentUserDoc.exists()) {
            currentUserDoc = await getDoc(doc(db, "client", currentUserId));
          }
          if (currentUserDoc.exists()) {
            setCurrentUserData({ id: currentUserId, ...currentUserDoc.data() });
          } else {
            setError("Current user data not found.");
          }
          setAllUsers(usersData);

          const teamsQuery = query(
            collection(db, "teams"),
            where("ownerId", "==", currentUserId)
          );
          const teamsSnapshot = await getDocs(teamsQuery);
          const teamsData = teamsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setTeams(teamsData);
        } catch (err) {
          console.error("Error loading data:", err);
          setError("Failed to load users or teams.");
        } finally {
          setLoading(false);
        }
      };
      loadData();
    } else {
      setSelectedOption({ type: "search", value: "name" });
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setAllUsers([]);
      setTeams([]);
      setDisplayedUsers([]);
      setSelectedUsers(new Set());
      setExternalNumbers("");
      setExternalInput("");
      setLoading(true);
      setError(null);
    }
  }, [showModal, currentUserId]);

  useEffect(() => {
    if (selectedOption.type === "search") {
      if (debouncedSearchTerm) {
        const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase().trim();
        const filtered = allUsers.filter((user) => {
          const name = user.name?.toLowerCase() || "";
          const email = user.email?.toLowerCase() || "";
          const mobile = user.mobileNumber?.replace(/\D/g, "") || "";
          const normalizedSearch = lowerCaseSearchTerm.replace(/\D/g, "");

          switch (selectedOption.value) {
            case "email":
              return email.includes(lowerCaseSearchTerm);
            case "mobile":
              return mobile.includes(normalizedSearch);
            case "name":
            default:
              return name.includes(lowerCaseSearchTerm);
          }
        });
        setDisplayedUsers(filtered);
      } else {
        setDisplayedUsers([]);
      }
    }
  }, [selectedOption, debouncedSearchTerm, allUsers]);

  useEffect(() => {
    if (selectedOption.type === "team") {
      const fetchTeamMembers = async () => {
        setLoading(true);
        try {
          const teamDocRef = doc(db, "teams", selectedOption.value);
          const teamDocSnap = await getDoc(teamDocRef);
          if (teamDocSnap.exists()) {
            const memberIds = teamDocSnap.data().members || [];
            if (memberIds.length > 0) {
              const memberPromises = memberIds.map((id) =>
                getDoc(doc(db, "lawyers", id))
              );
              const memberDocs = await Promise.all(memberPromises);
              const membersData = memberDocs
                .filter((docSnap) => docSnap.exists())
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
              setDisplayedUsers(membersData);
            } else {
              setDisplayedUsers([]);
            }
          } else {
            setError("Selected team not found.");
            setDisplayedUsers([]);
          }
        } catch (err) {
          console.error("Error fetching team members:", err);
          setError("Failed to load team members.");
          setDisplayedUsers([]);
        } finally {
          setLoading(false);
        }
      };
      fetchTeamMembers();
    }
  }, [selectedOption]);

  const handleUserSelectionChange = (userId, isChecked) => {
    setSelectedUsers((prev) => {
      const newSelected = new Set(prev);
      if (isChecked) newSelected.add(userId);
      else newSelected.delete(userId);
      return newSelected;
    });
  };

  const handleSelectAllChange = (isChecked) => {
    setSelectedUsers((prev) => {
      const newSelected = new Set(prev);
      if (isChecked) {
        displayedUsers.forEach((user) => newSelected.add(user.id));
      } else {
        displayedUsers.forEach((user) => newSelected.delete(user.id));
      }
      return newSelected;
    });
  };

  const handleSendClick = () => {
    let reminderDateTime;

    if (useSpecificTime) {
      if (!reminderDate || !reminderTime) {
        setError("Please select both date and time for the reminder.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      const reminderDateTimeString = `${reminderDate}T${reminderTime}:00`;
      reminderDateTime = new Date(reminderDateTimeString);
      if (isNaN(reminderDateTime.getTime())) {
        setError("Invalid reminder date or time format.");
        setTimeout(() => setError(null), 3000);
        return;
      }
    } else {
      const offsetMinutes = calculateOffsetMinutes();
      if (offsetMinutes <= 0) {
        setError("Invalid custom offset.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      reminderDateTime = new Date(event.time1 - offsetMinutes * 60 * 1000);
      const now = new Date();
      if (reminderDateTime <= now) {
        setError("Reminder time is in the past.");
        setTimeout(() => setError(null), 3000);
        return;
      }
    }

    if (sendToSelfOnly) {
      if (!currentUserData) {
        setError("Current user data not available.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      const reminderData = {
        event,
        recipients: [currentUserData],
        reminderTime: reminderDateTime.getTime(),
      };
      onSendReminders(reminderData);
    } else if (selectedOption.type === "external") {
      const numbers = externalNumbers.split(",").map((n) => n.trim());
      const validNumbers = numbers.filter(isValidPhoneNumber);
      if (validNumbers.length === 0) {
        setError("Please enter at least one valid phone number starting with + and digits.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      const reminderData = {
        event,
        recipients: validNumbers,
        reminderTime: reminderDateTime.getTime(),
      };
      onSendReminders(reminderData);
    } else {
      if (selectedUsers.size === 0) {
        setError("Please select at least one user.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      const usersToSend = allUsers.filter((user) => selectedUsers.has(user.id));
      const reminderData = {
        event,
        recipients: usersToSend,
        reminderTime: reminderDateTime.getTime(),
      };
      onSendReminders(reminderData);
    }
  };

  const isAllDisplayedSelected =
    displayedUsers.length > 0 &&
    displayedUsers.every((user) => selectedUsers.has(user.id));

  // Resolve selected user ids to full user objects (look in both the full list
  // and the currently displayed list so team members are covered too).
  const selectedUserList = Array.from(selectedUsers)
    .map(
      (id) =>
        allUsers.find((u) => u.id === id) ||
        displayedUsers.find((u) => u.id === id)
    )
    .filter(Boolean);

  const renderUserDetails = (user) => {
    if (!user) return null;
    return (
      <div className="flex-1 mr-4 text-sm">
        <p className="font-semibold text-gray-100">{user.name || "N/A"}</p>
        {user.email && <p className="text-gray-400 truncate">{user.email}</p>}
        {user.mobileNumber && (
          <p className="text-gray-400">{user.mobileNumber}</p>
        )}
      </div>
    );
  };

  const teamOptions = teams
    .filter((team) => !team.isDefault)
    .map((team) => ({
      label: `Team: ${team.teamName}`,
      value: `team-${team.id}`,
    }));
  const allOptions = [...searchOptions, ...teamOptions];

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue.startsWith("search-")) {
      const searchType = selectedValue.split("-")[1];
      setSelectedOption({ type: "search", value: searchType });
    } else if (selectedValue.startsWith("team-")) {
      const teamId = selectedValue.split("-")[1];
      setSelectedOption({ type: "team", value: teamId });
      setSearchTerm("");
    } else if (selectedValue === "external") {
      setSelectedOption({ type: "external" });
      setSearchTerm("");
      setDisplayedUsers([]);
      setSelectedUsers(new Set());
    } else {
      setSelectedOption({ type: "search", value: "name" });
    }
  };

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[1100] p-4 backdrop-blur-sm"
      onClick={() => setShowModal(false)}
    >
      <div
        className="bg-gray-800 rounded-lg shadow-xl p-4 w-full max-w-md max-h-[90vh] flex flex-col text-white border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-600">
          <h2 className="text-base font-semibold">Send Event Reminder</h2>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="reminder-event mb-3 p-2 bg-gray-700/80 rounded border border-gray-600">
          <p className="reminder-event-title font-medium text-yellow-400 text-sm">{event.title}</p>
          <p className="reminder-event-date text-xs text-gray-300">
            {format(new Date(event.date), "EEEE, MMMM d, yyyy")} at {event.time}
          </p>
          {event.isGoogleEvent && (
            <span className="text-xs text-blue-300">(Google Event)</span>
          )}
        </div>

        <div className="mb-2">
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={sendToSelfOnly}
              onChange={(e) => setSendToSelfOnly(e.target.checked)}
              className="mr-2"
            />
            Send to myself only
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-3 relative">
          <input
            type="text"
            placeholder={
              selectedOption.type === "search"
                ? `Enter ${selectedOption.value.charAt(0).toUpperCase() + selectedOption.value.slice(1)}`
                : "Select Members"
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={
              sendToSelfOnly ||
              selectedOption.type === "team" ||
              selectedOption.type === "external"
            }
            className={`flex-1 min-w-0 w-full p-2 pl-3 rounded bg-gray-700 border border-gray-600 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
              sendToSelfOnly ||
              selectedOption.type === "team" ||
              selectedOption.type === "external"
                ? "opacity-50"
                : ""
            }`}
          />
          <SelectMenu
            value={
              selectedOption
                ? `${selectedOption.type}-${selectedOption.value}`
                : ""
            }
            disabled={sendToSelfOnly}
            onChange={handleSelectChange}
            className={`flex-1 min-w-0 w-full p-2 rounded bg-gray-700 border border-gray-600 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 truncate ${
              sendToSelfOnly ? "opacity-50" : ""
            }`}
          >
            <option value="">Select...</option>
            {allOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectMenu>
        </div>

        <div className="flex-grow overflow-y-auto mb-3 border border-gray-700 rounded bg-gray-900/30 min-h-[150px] max-h-[200px] flex flex-col">
          {sendToSelfOnly ? (
            <div className="flex-grow flex items-center justify-center text-gray-400">
              Reminder will be sent to yourself only.
            </div>
          ) : (
            <>
              {selectedOption.type === "external" ? (
                <div className="p-2 h-full flex flex-col gap-2 overflow-y-auto">
                  {validExternalNumbers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {validExternalNumbers.map((number) => (
                        <span
                          key={number}
                          className="inline-flex items-center gap-1 bg-blue-600/30 border border-blue-500/50 text-blue-100 text-xs rounded-full pl-3 pr-1 py-1"
                        >
                          {number}
                          <button
                            type="button"
                            onClick={() => removeExternalNumber(number)}
                            className="hover:bg-blue-500/40 rounded-full p-0.5"
                            aria-label={`Remove ${number}`}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={externalInput}
                    onChange={(e) => setExternalInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        commitExternalInput();
                      }
                    }}
                    onBlur={commitExternalInput}
                    placeholder="Type a number e.g. +1234567890, then press Enter"
                    className="w-full p-2 rounded bg-gray-700 border border-gray-600 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {validExternalNumbers.length === 0 && (
                    <p className="text-xs text-gray-500">
                      Numbers must start with + followed by digits. Press Enter or
                      click outside to add.
                    </p>
                  )}
                </div>
              ) : loading ? (
                <div className="flex-grow flex items-center justify-center text-gray-400">
                  <Loader2 className="animate-spin mr-2" size={20} /> Loading...
                </div>
              ) : error ? (
                <div className="flex-grow flex items-center justify-center text-red-400 p-4 text-center">
                  Error: {error}
                </div>
              ) : displayedUsers.length === 0 ? (
                selectedUserList.length > 0 ? (
                  <div className="p-3">
                    <p className="text-xs font-medium text-gray-400 mb-2">
                      Selected Members ({selectedUserList.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedUserList.map((user) => (
                        <span
                          key={user.id}
                          className="inline-flex items-center gap-2 bg-blue-600/30 border border-blue-500/50 text-blue-100 text-xs rounded-full pl-3 pr-1 py-1"
                        >
                          <span className="flex flex-col min-w-0">
                            <span className="truncate max-w-[200px] font-medium">
                              {user.name || "User"}
                            </span>
                            {(user.email || user.mobileNumber) && (
                              <span className="truncate max-w-[200px] text-[10px] text-blue-200/80">
                                {user.email || user.mobileNumber}
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleUserSelectionChange(user.id, false)
                            }
                            className="hover:bg-blue-500/40 rounded-full p-0.5 flex-shrink-0"
                            aria-label="Remove member"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    {debouncedSearchTerm && (
                      <p className="text-center text-gray-500 mt-4 text-xs">
                        No users found.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 pt-10 px-4">
                    {selectedOption.type === "team"
                      ? "This team has no members."
                      : debouncedSearchTerm
                        ? "No users found."
                        : ""}
                  </p>
                )
              ) : (
                <>
                  <div className="sticky top-0 bg-gray-800 z-10 flex items-center px-3 py-2 border-b border-gray-700">
                    <input
                      type="checkbox"
                      id="select-all-reminder"
                      checked={isAllDisplayedSelected}
                      onChange={(e) => handleSelectAllChange(e.target.checked)}
                      className="mr-3 h-4 w-4 rounded text-blue-500 focus:ring-blue-600 focus:ring-offset-0 bg-gray-600 border-gray-500 cursor-pointer"
                    />
                    <label
                      htmlFor="select-all-reminder"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Select All ({displayedUsers.length} displayed /{" "}
                      {selectedUsers.size} selected)
                    </label>
                  </div>
                  <div className="flex-grow">
                    {displayedUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 border-b border-gray-700/50 hover:bg-gray-700/60 transition-colors"
                      >
                        <div className="flex items-center flex-grow overflow-hidden">
                          <input
                            type="checkbox"
                            id={`user-reminder-${user.id}`}
                            checked={selectedUsers.has(user.id)}
                            onChange={(e) =>
                              handleUserSelectionChange(
                                user.id,
                                e.target.checked
                              )
                            }
                            className="mr-3 h-4 w-4 rounded text-blue-500 focus:ring-blue-600 focus:ring-offset-0 bg-gray-600 border-gray-500 flex-shrink-0 cursor-pointer"
                          />
                          <label
                            htmlFor={`user-reminder-${user.id}`}
                            className="flex-grow cursor-pointer overflow-hidden"
                          >
                            {renderUserDetails(user)}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {event["Case No"] && (
          <div className="mb-2">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={useSpecificTime}
                onChange={(e) => setUseSpecificTime(e.target.checked)}
                className="mr-2"
              />
              Set specific reminder time
            </label>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Send Reminder:
          </label>
          {useSpecificTime ? (
            <div className="flex gap-2">
              <input
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <SelectMenu
                value={useCustomOffset ? "custom" : reminderOffsetMinutes}
                onChange={(e) => {
                  if (e.target.value === "custom") {
                    setUseCustomOffset(true);
                  } else {
                    setUseCustomOffset(false);
                    setReminderOffsetMinutes(parseInt(e.target.value, 10));
                  }
                }}
                className="p-2 rounded bg-gray-700 border border-gray-600"
              >
                {reminderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </SelectMenu>
              {useCustomOffset && (
                <>
                  <input
                    type="number"
                    value={customOffset}
                    onChange={(e) => setCustomOffset(e.target.value)}
                    placeholder="Enter number"
                    min="1"
                    className="p-2 rounded bg-gray-700 border border-gray-600"
                  />
                  <SelectMenu
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                    className="p-2 rounded bg-gray-700 border border-gray-600"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </SelectMenu>
                </>
              )}
            </div>
          )}
        </div>

        {error && !loading && (
          <p className="text-red-400 text-sm text-center mb-2">{error}</p>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-600">
          <button
            onClick={() => setShowModal(false)}
            disabled={isSending}
            className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-500 transition-colors text-white text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSendClick}
            disabled={
              (sendToSelfOnly && (loading || isSending)) ||
              (!sendToSelfOnly &&
                ((selectedOption.type !== "external" &&
                  selectedUsers.size === 0) ||
                  (selectedOption.type === "external" &&
                    validExternalNumbers.length === 0) ||
                  loading ||
                  isSending))
            }
            className={`px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 transition-colors text-white text-sm font-medium flex items-center justify-center gap-2 min-w-[150px] ${
              (sendToSelfOnly && (loading || isSending)) ||
              (!sendToSelfOnly &&
                ((selectedOption.type !== "external" &&
                  selectedUsers.size === 0) ||
                  (selectedOption.type === "external" &&
                    validExternalNumbers.length === 0) ||
                  loading ||
                  isSending))
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {isSending ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Scheduling...
              </>
            ) : sendToSelfOnly ? (
              "Schedule"
            ) : selectedOption.type === "external" ? (
              `Schedule (${validExternalNumbers.length})`
            ) : (
              `Schedule (${selectedUsers.size})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReminderModal;
